import { supabase } from './supabaseClient';
import { CounterSaleDiscountInput } from './productSales';
import { CardRefundLeg, RefundDestination, finishCardRefundLegs } from './refunds';

// ─────────────────────────────────────────────────────────────────────────────
// Customer orders, tracked on two independent dimensions (Shopify-style):
//  • Fulfillment status: new → confirmed → out_for_delivery → delivered
//    (+ cancelled). Stored on the order row.
//  • Payment state: derived from the linked invoice (unpaid / invoice due /
//    partial / paid / void). Never stored as an order status — a
//    pay-at-checkout order is "new · paid", an invoice-on-delivery order is
//    "delivered · invoice due" until the balance clears.
// ─────────────────────────────────────────────────────────────────────────────

export type CustomerOrderStatus =
  | 'new'
  | 'confirmed'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled';

export type CustomerOrderSource = 'store' | 'portal' | 'staff';

export interface CustomerOrderItem {
  id: string;
  order_id: string;
  business_id: string;
  /** NULL for priced lines with no product (e.g. delivery fees). */
  stock_item_id: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  /** 'product' (default) | 'fee' (delivery/surcharges) | 'deposit' (bottle deposits) | 'rental' (recurring rental first period). */
  line_type?: 'product' | 'fee' | 'deposit' | 'rental';
  deposit_stock_item_id?: string | null;
  created_at: string;
}

/** Slim invoice snapshot joined onto orders for the payment dimension. */
export interface OrderInvoiceInfo {
  id: string;
  invoice_number: string | null;
  status: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  tax_breakdown: Array<{ name: string; rate_bps: number; amount_cents: number; base_cents?: number | null }> | null;
}

export interface CustomerOrder {
  id: string;
  business_id: string;
  customer_id: string | null;
  order_number: string;
  status: CustomerOrderStatus;
  source: CustomerOrderSource;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  fulfillment_type: 'delivery' | 'pickup';
  delivery_address: string | null;
  /** Saved address deposits on this order are checked against. Null for pickup and older orders. */
  delivery_address_id?: string | null;
  delivery_notes: string | null;
  requested_date: string | null;
  requested_window: string | null;
  estimated_subtotal_cents: number;
  estimated_discount_cents: number;
  estimated_tax_cents: number;
  estimated_total_cents: number;
  discount: CounterSaleDiscountInput | null;
  customer_note: string | null;
  /** Customer PO reference. Copied onto the invoice when the order is invoiced. */
  po_number?: string | null;
  internal_notes: string | null;
  invoice_id: string | null;
  product_sale_id: string | null;
  /** Planned pickup: [{stock_item_id, returned, lost?}] — editable until invoiced, default at invoicing. */
  expected_returns?: Array<{ stock_item_id: string; returned: number; lost?: number }> | null;
  /** Structured address snapshot from checkout (postal code drives area pricing). */
  shipping_address?: {
    company?: string;
    line1?: string;
    line2?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    country?: string;
    formatted?: string;
  } | null;
  /** Proof-of-delivery photos snapped by the driver at the Deliver step. */
  delivery_photos?: DeliveryPhoto[] | null;
  /** Staff mute: while true, no customer notifications send for this order
   *  (pipeline emails, auto invoice email, new-order push). Toggle anytime. */
  suppress_notifications?: boolean;
  placed_at: string;
  confirmed_at: string | null;
  out_for_delivery_at: string | null;
  delivered_at: string | null;
  invoiced_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  items?: CustomerOrderItem[];
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  invoice?: OrderInvoiceInfo | null;
}

const ORDER_SELECT =
  '*, items:customer_order_items(*), customer:customers(id, first_name, last_name, email, phone), invoice:invoices(id, invoice_number, status, subtotal_cents, discount_cents, tax_cents, total_cents, amount_paid_cents, amount_due_cents, tax_breakdown)';

export interface DeliveryPhoto {
  path: string;
  url: string;
  taken_at: string;
}

/** Upload one proof-of-delivery photo and return its entry (not yet saved on the order). */
export async function uploadDeliveryPhoto(orderId: string, file: File): Promise<DeliveryPhoto> {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${orderId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from('delivery-photos')
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from('delivery-photos').getPublicUrl(path);
  return { path, url: data.publicUrl, taken_at: new Date().toISOString() };
}

/** Persist the photo list on the order (photos survive even if delivery is abandoned). */
export async function saveDeliveryPhotos(orderId: string, photos: DeliveryPhoto[]): Promise<void> {
  const { error } = await supabase
    .from('customer_orders')
    .update({ delivery_photos: photos, updated_at: new Date().toISOString() })
    .eq('id', orderId);
  if (error) throw error;
}

/** Remove a photo from storage; caller updates the order list via saveDeliveryPhotos. */
export async function deleteDeliveryPhotoFile(path: string): Promise<void> {
  await supabase.storage.from('delivery-photos').remove([path]);
}

// "Active" = goods not yet delivered. Delivered orders drop out of Active even
// if their invoice is still unpaid (use the Unpaid filter to chase money).
const ACTIVE_STATUSES: CustomerOrderStatus[] = ['new', 'confirmed', 'out_for_delivery'];

export async function fetchCustomerOrders(
  businessId: string,
  options?: { status?: CustomerOrderStatus | 'active' | 'all' }
): Promise<CustomerOrder[]> {
  let query = supabase
    .from('customer_orders')
    .select(ORDER_SELECT)
    .eq('business_id', businessId)
    .order('placed_at', { ascending: false });

  const status = options?.status ?? 'all';
  if (status === 'active') {
    query = query.in('status', ACTIVE_STATUSES);
  } else if (status !== 'all') {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as unknown as CustomerOrder[];
}

/** All of one customer's orders (Customer Details → Orders tab). */
export async function fetchCustomerOrdersForCustomer(
  businessId: string,
  customerId: string
): Promise<CustomerOrder[]> {
  const { data, error } = await supabase
    .from('customer_orders')
    .select(ORDER_SELECT)
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .order('placed_at', { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as CustomerOrder[];
}

export interface CreateCustomerOrderInput {
  businessId: string;
  items: Array<{
    stock_item_id: string | null;
    product_name: string;
    quantity: number;
    unit_price: number;
    /** 'product' (default) | 'fee' (delivery/surcharges) | 'deposit' (bottle deposits) | 'rental' (recurring rental first period). */
    line_type?: 'product' | 'fee' | 'deposit' | 'rental';
    /** For deposit lines: the container product the deposit belongs to. */
    deposit_stock_item_id?: string | null;
    /** Quote-time override (deposit lines are non-taxable). */
    taxable?: boolean;
  }>;
  customerId?: string | null;
  contact?: { name?: string | null; email?: string | null; phone?: string | null } | null;
  fulfillment?: {
    type?: 'delivery' | 'pickup';
    address?: string | null;
    notes?: string | null;
    requested_date?: string | null;
    requested_window?: string | null;
    /** Container swaps assumed at order time: [{ stock_item_id, returned }]. */
    expected_returns?: Array<{ stock_item_id: string; returned: number }> | null;
    /** Mute all customer notifications for this order from the moment it's created. */
    suppress_notifications?: boolean;
  } | null;
  discount?: CounterSaleDiscountInput | null;
  source?: CustomerOrderSource;
  customerNote?: string | null;
  /** Optional customer PO. Printed on the invoice generated from this order. */
  poNumber?: string | null;
  /** Saved address book row. Deposits on a delivery are checked against this site. */
  deliveryAddressId?: string | null;
  /** Structured shipping address snapshot (same shape as online orders). */
  shippingAddress?: {
    company?: string;
    line1: string;
    line2?: string;
    city: string;
    province: string;
    postal_code: string;
    country: string;
    formatted: string;
  } | null;
}

export interface CreateCustomerOrderResult {
  order_id: string;
  order_number: string;
  estimated_subtotal_cents: number;
  estimated_discount_cents: number;
  estimated_tax_cents: number;
  estimated_total_cents: number;
}

export async function createCustomerOrder(
  input: CreateCustomerOrderInput
): Promise<CreateCustomerOrderResult> {
  const { data, error } = await supabase.rpc('create_customer_order', {
    p_business_id: input.businessId,
    p_items: input.items,
    p_customer_id: input.customerId ?? null,
    p_contact: input.contact ?? null,
    p_fulfillment: input.fulfillment ?? null,
    p_discount: input.discount ?? null,
    p_source: input.source ?? 'staff',
    p_customer_note: input.customerNote ?? null,
  });
  if (error) throw error;
  const result = data as CreateCustomerOrderResult;
  const poNumber = (input.poNumber ?? '').trim();
  // Structured address snapshot lives outside the RPC (same post-create update
  // the store-checkout edge function performs). The customer PO does too.
  if ((input.shippingAddress || input.deliveryAddressId || poNumber) && result?.order_id) {
    const { error: snapError } = await supabase
      .from('customer_orders')
      .update({
        ...(input.shippingAddress ? { shipping_address: input.shippingAddress } : {}),
        ...(input.deliveryAddressId ? { delivery_address_id: input.deliveryAddressId } : {}),
        ...(poNumber ? { po_number: poNumber } : {}),
      })
      .eq('id', result.order_id);
    if (snapError) throw snapError;
  }
  // Staff orders are created already CONFIRMED (no manual confirm step), so
  // the customer's order-confirmed email goes out now instead of on the old
  // "Mark confirmed" click. The edge function still honors the per-order mute
  // and the store's notification toggles.
  if ((input.source ?? 'staff') === 'staff' && result?.order_id) {
    notifyOrderEvent(result.order_id, 'order_confirmed');
  }
  return result;
}

/**
 * Toggle the per-order notification mute. While muted, the notification edge
 * function skips every pipeline email for the order and the deliver flow skips
 * the auto invoice email; unmuting lets future events send as normal (events
 * that occurred while muted are not sent retroactively).
 */
export async function setOrderNotificationsMuted(orderId: string, muted: boolean): Promise<void> {
  const { error } = await supabase
    .from('customer_orders')
    .update({ suppress_notifications: muted })
    .eq('id', orderId);
  if (error) throw error;
}

/**
 * Fire-and-forget customer email for a pipeline event. The edge function
 * checks the business's per-event toggles and a sent-once guard, so calling
 * it unconditionally is safe (it just no-ops when the email is disabled).
 */
function notifyOrderEvent(
  orderId: string,
  event: 'order_confirmed' | 'out_for_delivery' | 'delivered' | 'cancelled'
): void {
  supabase.functions
    .invoke('send-order-notification', { body: { orderId, event } })
    .catch(() => {}); // never block or fail the staff action over an email
}

const STATUS_NOTIFICATION_EVENT: Partial<Record<CustomerOrderStatus, Parameters<typeof notifyOrderEvent>[1]>> = {
  confirmed: 'order_confirmed',
  out_for_delivery: 'out_for_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
};

export async function updateCustomerOrderStatus(
  orderId: string,
  status: CustomerOrderStatus
): Promise<void> {
  const { error } = await supabase.rpc('update_customer_order_status', {
    p_order_id: orderId,
    p_status: status,
  });
  if (error) throw error;
  const event = STATUS_NOTIFICATION_EVENT[status];
  if (event) notifyOrderEvent(orderId, event);
}

// ── Order timeline ─────────────────────────────────────────────────────────────

export interface OrderEvent {
  id: string;
  order_id: string;
  event_type: string;
  message: string;
  details: Record<string, unknown> | null;
  actor_id: string | null;
  created_at: string;
}

export async function fetchOrderEvents(orderId: string): Promise<OrderEvent[]> {
  const { data, error } = await supabase
    .from('customer_order_events')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as OrderEvent[];
}

/**
 * Stable display order for order lines: delivery fees first, then products
 * (oldest first, so rows never jump when quantities change), then deposit
 * lines at the bottom.
 */
export function sortOrderItems<T extends CustomerOrderItem>(items: T[]): T[] {
  const rank = (i: T) => {
    const t = i.line_type ?? 'product';
    return t === 'fee' ? 0 : t === 'product' ? 1 : t === 'rental' ? 2 : 3;
  };
  return [...items].sort((a, b) => {
    const byType = rank(a) - rank(b);
    if (byType !== 0) return byType;
    const byCreated = (a.created_at || '').localeCompare(b.created_at || '');
    if (byCreated !== 0) return byCreated;
    return a.id.localeCompare(b.id);
  });
}

export interface ReopenBillingResult {
  order_id: string;
  voided_invoice_id: string;
  wallet_refunded_cents: number;
  card_refunded_cents: number;
  cash_refunded_cents: number;
  refund_group_id: string | null;
  card_refund_legs: CardRefundLeg[];
  deposit_credit_reversed_cents: number;
  pending_payments_voided: number;
  container_rows_removed: number;
  stock_movements_removed: number;
  rentals_reverted?: number;
}

/**
 * Unwinds an invoiced order's billing: voids the invoice, returns collected
 * money to the customer (wallet credit by default, or back to card / cash /
 * e-transfer through the refund engine), restores stock + container
 * movements, and unlocks the order for editing / re-invoicing. Card legs are
 * executed on Stripe before this resolves.
 */
export async function reopenOrderBilling(
  orderId: string,
  refund?: { destination: RefundDestination; reference?: string | null }
): Promise<ReopenBillingResult> {
  const { data, error } = await supabase.rpc('reopen_order_billing', {
    p_order_id: orderId,
    p_refund_destination: refund?.destination ?? 'wallet',
    p_refund_reference: refund?.reference ?? null,
  });
  if (error) throw error;
  const result = { ...(data as ReopenBillingResult), card_refund_legs: (data?.card_refund_legs ?? []) as CardRefundLeg[] };
  const processed = await finishCardRefundLegs(result);
  if (processed) {
    result.card_refunded_cents = processed.card_refunded_cents;
    result.wallet_refunded_cents += processed.wallet_credited_cents;
  }
  return result;
}

export interface InvoiceOrderPayment {
  method: string;
  amount?: number;
  reference?: string;
  gift_card_id?: string;
}

// ── Container deposits (returnable packaging, e.g. water jugs) ────────────────

/** Actual empties collected at delivery, per deposit-bearing product. */
export interface OrderReturnInput {
  stock_item_id: string;
  returned: number;
  lost?: number;
}

/** Server-computed deposit math for one deposit-bearing product on an order. */
export interface OrderDepositSummary {
  stock_item_id: string;
  product_name: string;
  /** Per-unit deposit in cents. */
  deposit_cents: number;
  delivered: number;
  returned: number;
  lost: number;
  /** New deposits charged on the invoice (delivered − returned, floor 0). */
  net_new: number;
  /** Deposit refunded to the wallet when returns exceed deliveries. */
  excess_refund_cents: number;
  /** Containers the customer held BEFORE this delivery (returns are capped by it). */
  prior_balance?: number;
  /**
   * Picked-up containers that weren't on record (e.g. imported customers).
   * Recorded as a $0-deposit count correction so the balance reflects the
   * jugs actually in the field.
   */
  untracked?: number;
  /** Returned jugs refunded at today's rate because no deposit was on record for them. */
  unmatched?: number;
  unmatched_rate_cents?: number;
}

export interface OrderInvoiceQuote {
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  deposits: OrderDepositSummary[];
}

/**
 * Quote what invoicing this order would produce, with deposit lines rebuilt
 * from actual returns. Pass returns=null to use the checkout swap assumption.
 */
export async function quoteCustomerOrderInvoice(
  orderId: string,
  returns?: OrderReturnInput[] | null
): Promise<OrderInvoiceQuote> {
  const { data, error } = await supabase.rpc('quote_customer_order_invoice', {
    p_order_id: orderId,
    p_returns: returns ?? null,
  });
  if (error) throw error;
  return {
    subtotalCents: Number(data?.subtotal_cents ?? 0),
    discountCents: Number(data?.discount_cents ?? 0),
    taxCents: Number(data?.tax_cents ?? 0),
    totalCents: Number(data?.total_cents ?? 0),
    deposits: (data?.deposits ?? []) as OrderDepositSummary[],
  };
}

export interface ContainerBalance {
  stock_item_id: string;
  quantity: number;
  deposit_cents_held: number;
  item_name?: string;
}

/** Containers a customer currently holds (per deposit-bearing product). */
export async function getContainerBalances(customerId: string): Promise<ContainerBalance[]> {
  const { data, error } = await supabase
    .from('customer_container_balances')
    .select('stock_item_id, quantity, deposit_cents_held')
    .eq('customer_id', customerId);
  if (error) throw error;
  return (data || []).filter(r => r.quantity !== 0 || r.deposit_cents_held !== 0);
}

/**
 * Manual on-hand correction (signed delta). Records an auditable 'adjustment'
 * ledger row; a reason is mandatory and the balance can never go negative.
 * Deposit value moves only when depositCentsPerUnit is set (> 0).
 */
export async function adjustContainerBalance(input: {
  businessId: string;
  customerId: string;
  stockItemId: string;
  delta: number;
  notes: string;
  depositCentsPerUnit?: number;
}): Promise<{ balance: number; item_name: string }> {
  const { data, error } = await supabase.rpc('adjust_container_balance', {
    p_business_id: input.businessId,
    p_customer_id: input.customerId,
    p_stock_item_id: input.stockItemId,
    p_delta: input.delta,
    p_notes: input.notes,
    p_deposit_cents_per_unit: input.depositCentsPerUnit ?? 0,
  });
  if (error) throw error;
  return data as { balance: number; item_name: string };
}

/** Move unassigned containers onto a saved delivery address. Does not refund. */
export async function assignContainersToAddress(input: {
  businessId: string;
  customerId: string;
  stockItemId: string;
  addressId: string;
  quantity: number;
}): Promise<{ moved: number; item_name: string; address_id: string }> {
  const { data, error } = await supabase.rpc('assign_containers_to_address', {
    p_business_id: input.businessId,
    p_customer_id: input.customerId,
    p_stock_item_id: input.stockItemId,
    p_address_id: input.addressId,
    p_quantity: input.quantity,
  });
  if (error) throw error;
  return data as { moved: number; item_name: string; address_id: string };
}

/** One FIFO lot consumed by a refund: which delivery, at what rate. */
export interface DepositRefundLot {
  source_tx: string;
  order_id: string | null;
  invoice_id: string | null;
  rate_cents: number;
  quantity: number;
  refund_cents: number;
  charged_at?: string;
}

export interface DepositRefundQuote {
  allocation: DepositRefundLot[];
  /** Exact payout for the requested quantity, at the rates originally paid. */
  refund_cents: number;
  /** Units with no deposit on record — refunded at today's rate. */
  unmatched_quantity: number;
  unmatched_rate_cents?: number;
  held_quantity: number;
  held_value_cents: number;
}

/**
 * Preview a deposit refund before recording it: consumes the customer's
 * deposit lots FIFO and prices each at the rate they actually paid.
 */
export async function quoteContainerRefund(input: {
  businessId: string;
  customerId: string;
  stockItemId: string;
  quantity: number;
  /** Site whose deposits this return spends. Omit to include every site. */
  addressId?: string | null;
  /** Counter / no site: only containers not tied to an address. */
  unassignedOnly?: boolean;
}): Promise<DepositRefundQuote> {
  const { data, error } = await supabase.rpc('quote_container_refund', {
    p_business_id: input.businessId,
    p_customer_id: input.customerId,
    p_stock_item_id: input.stockItemId,
    p_quantity: input.quantity,
    p_address_id: input.addressId ?? null,
    p_unassigned_only: input.unassignedOnly === true,
  });
  if (error) throw error;
  return data as DepositRefundQuote;
}

export interface ContainerReturnResult {
  balance: number;
  refund_cents: number;
  refund_method: string;
  allocation: DepositRefundLot[];
  refund_group_id?: string | null;
  card_refunds?: CardRefundLeg[];
  /** How much actually went back to a card (after Stripe ran). */
  card_refunded_cents?: number;
  /** Wallet credit: chosen, or the fallback when no card money was refundable. */
  wallet_credited_cents?: number;
  /** Cash / e-transfer recorded as money out. */
  cash_refunded_cents?: number;
}

/**
 * Standalone container pickup (no new order): refund returned containers to
 * the wallet, back to a card they paid with (real Stripe refund), or in cash /
 * e-transfer — and/or write off lost ones. Refunds are always priced from the
 * customer's deposit lots (the rates they actually paid) and go through the
 * shared refund engine, so they land in the refund ledger.
 */
export async function recordContainerReturn(input: {
  businessId: string;
  customerId: string;
  stockItemId: string;
  returned: number;
  lost: number;
  refundMethod: RefundDestination | 'external';
  notes?: string;
  /** Site whose deposits this return spends. Omit to include every site. */
  addressId?: string | null;
  /** Counter / no site: only containers not tied to an address. */
  unassignedOnly?: boolean;
}): Promise<ContainerReturnResult> {
  const { data, error } = await supabase.rpc('record_container_return', {
    p_business_id: input.businessId,
    p_customer_id: input.customerId,
    p_stock_item_id: input.stockItemId,
    p_returned: input.returned,
    p_lost: input.lost,
    p_refund_method: input.refundMethod === 'external' ? 'cash' : input.refundMethod,
    p_notes: input.notes ?? null,
    p_address_id: input.addressId ?? null,
    p_unassigned_only: input.unassignedOnly === true,
  });
  if (error) throw error;
  const result = data as ContainerReturnResult;
  const processed = await finishCardRefundLegs({
    refund_group_id: result.refund_group_id,
    card_refund_legs: result.card_refunds,
  });
  if (processed) {
    result.card_refunded_cents = processed.card_refunded_cents;
    result.wallet_credited_cents = (result.wallet_credited_cents ?? 0) + processed.wallet_credited_cents;
  }
  return result;
}

export interface InvoiceOrderResult {
  order_id: string;
  order_number: string;
  sale_id: string;
  sale_number: string;
  invoice_id: string;
  invoice_number: string;
  invoice_status: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  payment_id: string | null;
  deposits?: OrderDepositSummary[];
  /** Excess deposit refund credited to the customer's wallet, in cents. */
  wallet_credit_cents?: number;
  /** Excess deposit refund taken as a credit line on this invoice, in cents. */
  invoice_credit_cents?: number;
  /** Excess deposit refund handed back in cash / e-transfer, in cents. */
  external_refund_cents?: number;
  /** Excess deposit refund that went back to a card (after Stripe ran), in cents. */
  card_refund_cents?: number;
  refund_group_id?: string | null;
  card_refund_legs?: CardRefundLeg[];
  /** Where the excess deposit refund went: wallet | invoice | cash | e_transfer | card. */
  excess_refund_mode?: string;
  /** Wallet credit auto-applied to the invoice (wallet-first), in cents. */
  wallet_applied_cents?: number;
}

/** Where an excess-return deposit refund goes at delivery / the counter. */
export type ExcessRefundMode = 'wallet' | 'invoice' | 'external' | 'cash' | 'e_transfer' | 'card';

/**
 * Generate the invoice for an order (delivery-time). Delegates to the
 * counter-sale engine: stock deduction, business-rate tax, issued invoice,
 * optional immediate payment — all atomic. Pass payment=null to leave the
 * invoice open (collect via payment link / bank transfer later).
 */
export async function invoiceCustomerOrder(
  orderId: string,
  payment: InvoiceOrderPayment | null,
  expectedTotalCents?: number | null,
  returns?: OrderReturnInput[] | null,
  terms?: {
    /** Net term override for this invoice; null = customer/business default. */
    paymentTerms?: string | null;
    /** Explicit custom due date (stored as 'custom' terms). Wins over paymentTerms. */
    dueAt?: string | null;
  } | null,
  /**
   * Where an excess-return deposit refund goes: 'wallet' (default store
   * credit), 'invoice' (negative credit line on this invoice), 'card' (Stripe
   * refund onto a card they paid with here), or 'cash' / 'e_transfer'
   * (recorded money out).
   */
  excessRefundMode: ExcessRefundMode = 'wallet',
  /**
   * true (default) = Deliver & Invoice: the order is marked delivered and its
   * rentals activate. false = invoice ahead of delivery: same billing, order
   * stays in the pipeline; "Mark delivered" later activates rentals.
   */
  markDelivered: boolean = true
): Promise<InvoiceOrderResult> {
  const { data, error } = await supabase.rpc('invoice_customer_order', {
    p_order_id: orderId,
    p_payment: payment ?? null,
    p_expected_total_cents: expectedTotalCents ?? null,
    p_returns: returns ?? null,
    p_payment_terms: terms?.dueAt ? null : terms?.paymentTerms ?? null,
    p_due_at: terms?.dueAt ?? null,
    p_excess_refund_mode: excessRefundMode,
    p_mark_delivered: markDelivered,
  });
  if (error) throw error;
  // Delivering sends the delivered status email (send-once guarded
  // server-side). The invoice email itself is sent by the Deliver & Invoice
  // flow (PDF + payment link), not the notification pipeline.
  if (markDelivered) notifyOrderEvent(orderId, 'delivered');
  const result = { ...(data as InvoiceOrderResult), card_refund_legs: (data?.card_refund_legs ?? []) as CardRefundLeg[] };
  // Card legs were written pending inside the transaction — run them now.
  const processed = await finishCardRefundLegs(result);
  if (processed) {
    result.card_refund_cents = processed.card_refunded_cents;
    result.wallet_credit_cents = (result.wallet_credit_cents ?? 0) + processed.wallet_credited_cents;
  }
  return result;
}

// ── Item edits before invoicing (RLS: staff of the business) ─────────────────

export async function updateOrderItemQuantity(itemId: string, quantity: number): Promise<void> {
  const { error } = await supabase
    .from('customer_order_items')
    .update({ quantity })
    .eq('id', itemId);
  if (error) throw error;
}

export async function removeOrderItem(itemId: string): Promise<void> {
  const { error } = await supabase
    .from('customer_order_items')
    .delete()
    .eq('id', itemId);
  if (error) throw error;
}

export async function addOrderItem(
  orderId: string,
  businessId: string,
  item: {
    stock_item_id: string | null;
    product_name: string;
    quantity: number;
    unit_price: number;
    line_type?: 'product' | 'fee' | 'deposit' | 'rental';
    deposit_stock_item_id?: string | null;
  }
): Promise<void> {
  const { error } = await supabase.from('customer_order_items').insert({
    order_id: orderId,
    business_id: businessId,
    stock_item_id: item.stock_item_id,
    product_name: item.product_name,
    quantity: item.quantity,
    unit_price: item.unit_price,
    line_type: item.line_type ?? 'product',
    deposit_stock_item_id: item.deposit_stock_item_id ?? null,
  });
  if (error) throw error;
}

/**
 * Set/replace the delivery address on an order — used when a pickup order is
 * switched to delivery (it has no address yet). Also stores the postal code in
 * the structured shipping_address snapshot so area-based delivery pricing works.
 */
export async function updateOrderDeliveryAddress(
  orderId: string,
  address: string,
  postalCode?: string | null
): Promise<void> {
  const { data } = await supabase
    .from('customer_orders')
    .select('shipping_address')
    .eq('id', orderId)
    .single();
  const existing = (data?.shipping_address as Record<string, unknown> | null) || {};
  const { error } = await supabase
    .from('customer_orders')
    .update({
      delivery_address: address || null,
      shipping_address: {
        ...existing,
        line1: address || existing.line1 || undefined,
        postal_code: postalCode?.trim() || existing.postal_code || undefined,
      },
    })
    .eq('id', orderId);
  if (error) throw error;
}

/** Switch an order between delivery and pickup (fee lines are managed separately). */
export async function updateOrderFulfillmentType(
  orderId: string,
  type: 'delivery' | 'pickup'
): Promise<void> {
  const { error } = await supabase
    .from('customer_orders')
    .update({ fulfillment_type: type })
    .eq('id', orderId);
  if (error) throw error;
}

/**
 * Planned container pickup for an order that isn't invoiced yet: what the
 * customer says they'll hand back (and any lost). Persists on the order so it
 * survives closing/reopening, seeds the deposit preview lines, and is the
 * default at Deliver & Invoice / Invoice now.
 */
export async function updateOrderExpectedReturns(
  orderId: string,
  returns: OrderReturnInput[]
): Promise<void> {
  const { error } = await supabase
    .from('customer_orders')
    .update({ expected_returns: returns.length > 0 ? returns : null })
    .eq('id', orderId);
  if (error) throw error;
}

export async function updateOrderInternalNotes(orderId: string, notes: string): Promise<void> {
  const { error } = await supabase
    .from('customer_orders')
    .update({ internal_notes: notes || null })
    .eq('id', orderId);
  if (error) throw error;
}

/** Set or clear the customer PO on an order. Blank clears it. */
export async function updateOrderPoNumber(orderId: string, poNumber: string): Promise<void> {
  const { error } = await supabase
    .from('customer_orders')
    .update({ po_number: poNumber.trim() || null })
    .eq('id', orderId);
  if (error) throw error;
}

/**
 * Re-run the quote engine over the order's current items and store the fresh
 * estimated totals on the order row (used after staff edit items).
 */
export async function refreshOrderEstimate(order: CustomerOrder): Promise<void> {
  const items = (order.items || []).map(i => ({
    stock_item_id: i.stock_item_id,
    quantity: Number(i.quantity),
    unit_price: Number(i.unit_price),
    // Deposit lines are refundable and never taxed; without this the re-quote
    // would treat them as taxable (no stock item to carry tax_exempt).
    ...(i.line_type === 'deposit' ? { taxable: false } : {}),
  }));
  const { data, error } = await supabase.rpc('quote_counter_sale', {
    p_business_id: order.business_id,
    p_items: items,
    p_discount: order.discount ?? null,
  });
  if (error) throw error;
  const { error: updateError } = await supabase
    .from('customer_orders')
    .update({
      estimated_subtotal_cents: data.subtotal_cents ?? 0,
      estimated_discount_cents: data.discount_cents ?? 0,
      estimated_tax_cents: data.tax_cents ?? 0,
      estimated_total_cents: data.total_cents ?? 0,
    })
    .eq('id', order.id);
  if (updateError) throw updateError;
}

// ── Display helpers ───────────────────────────────────────────────────────────

const STATUS_LABELS: Record<CustomerOrderStatus, string> = {
  new: 'New',
  confirmed: 'Confirmed',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

// Pickup orders share the same underlying pipeline statuses, but the two
// fulfillment steps read differently: staged = ready for pickup, handed
// over = picked up. (Emails already branch this way server-side.)
const PICKUP_STATUS_LABELS: Partial<Record<CustomerOrderStatus, string>> = {
  out_for_delivery: 'Ready for pickup',
  delivered: 'Picked up',
};

export function orderStatusLabel(
  status: CustomerOrderStatus,
  fulfillmentType?: 'delivery' | 'pickup'
): string {
  // Legacy rows may still say 'invoiced' in cached data; show as completed.
  if ((status as string) === 'invoiced') return fulfillmentType === 'pickup' ? 'Picked up' : 'Delivered';
  if (fulfillmentType === 'pickup' && PICKUP_STATUS_LABELS[status]) return PICKUP_STATUS_LABELS[status]!;
  return STATUS_LABELS[status] ?? status;
}

/** The next pipeline step for the advance button (invoicing is separate). */
export function nextOrderStatus(
  status: CustomerOrderStatus
): Exclude<CustomerOrderStatus, 'cancelled'> | null {
  switch (status) {
    case 'new': return 'confirmed';
    case 'confirmed': return 'out_for_delivery';
    case 'out_for_delivery': return 'delivered';
    default: return null;
  }
}

// ── Payment dimension (derived from the linked invoice) ──────────────────────

export type OrderPaymentState = 'unpaid' | 'invoice_due' | 'partial' | 'paid' | 'void';

/**
 * Where the money stands for this order:
 *  • unpaid       — no invoice yet (typical invoice-on-delivery order in flight)
 *  • invoice_due  — invoice issued, nothing collected yet
 *  • partial      — invoice issued, partially collected
 *  • paid         — settled (at checkout, from wallet, or recorded by staff)
 *  • void         — invoice voided/refunded
 */
export function orderPaymentState(order: CustomerOrder): OrderPaymentState {
  const inv = order.invoice;
  if (!inv) return order.invoice_id ? 'invoice_due' : 'unpaid';
  if (inv.status === 'void') return 'void';
  const total = Number(inv.total_cents ?? 0);
  const paid = Number(inv.amount_paid_cents ?? 0);
  if (inv.status === 'paid' || (total > 0 && paid >= total)) return 'paid';
  if (paid > 0) return 'partial';
  return 'invoice_due';
}

export const ORDER_PAYMENT_LABELS: Record<OrderPaymentState, string> = {
  unpaid: 'Unpaid',
  invoice_due: 'Invoice due',
  partial: 'Partially paid',
  paid: 'Paid',
  void: 'Voided',
};

/** An order is fully settled when the goods and the money are both done. */
export function isOrderSettled(order: CustomerOrder): boolean {
  const pay = orderPaymentState(order);
  return order.status === 'delivered' && (pay === 'paid' || pay === 'void');
}
